import { NODE_API_MAX_PAGE_SIZE } from "#services/ServiceHelper";
import {
  EmissionsHomeViewFilterAreaSchemaKeys,
  type EmissionsHomeViewFilterAreaSchemaType,
} from "#src/batteries-included-components/FilterAreas/DashboardsFilterAreas/DashboardsFilterAreas";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { useStickyState } from "#src/hooks/useStickyState";
import { getIntervalInMonthsFormatted, hydrateDateRange } from "#utils/date";
import { useQuery } from "@tanstack/react-query";
import {
  MetricTile,
  NumberDataDisplay,
  StorageKeys,
  XYChart,
  useToast,
} from "@validereinc/common-components";
import { CalculatorResultsDomain } from "@validereinc/domain";
import { Measurements } from "@validereinc/domain-controllers";
import { formatNumberValueAndUnit } from "@validereinc/utilities";
import { compareAsc, format, parse } from "date-fns";
import React, { useMemo } from "react";

const { PollutantsEmitted } = Measurements.logic;

type DataPoint = {
  date: Date;
  year_month: string;
  measurement: Record<string, number>;
};

type DataSetType = {
  label: string;
  variant: string;
  axis: string;
  data: DataPoint[];
};

const BAR_CHART_HEIGHT = 300;
const pollutants = Object.values(PollutantsEmitted);
const sortFunction = (
  { date: aDate }: { date: Date },
  { date: bDate }: { date: Date }
) => compareAsc(aDate, bDate);

/**
 * Returns a bar chart of the emissions for the selected facility over time
 */
export const EmissionsHomeMetricTileXYChart = ({
  viewConfigStorageKey,
}: Pick<StorageKeys, "viewConfigStorageKey">) => {
  const { toast } = useToast();
  const { getUnitByType, getPrecisionByType, getUnitName } =
    useMeasurementTypes();
  const [
    {
      [EmissionsHomeViewFilterAreaSchemaKeys.date_range]: dateRange,
      [EmissionsHomeViewFilterAreaSchemaKeys.pollutant]: pollutant,
      [EmissionsHomeViewFilterAreaSchemaKeys.reporting_scenario]:
        reportingGroup,
    },
  ] = useStickyState<EmissionsHomeViewFilterAreaSchemaType>(
    {},
    viewConfigStorageKey
  );

  const { mass, label } = pollutant
    ? (PollutantsEmitted[pollutant] ?? PollutantsEmitted.co2_eq)
    : PollutantsEmitted.co2_eq;
  const precision = getPrecisionByType(mass);
  const unit = getUnitName(getUnitByType(mass));

  const title = `Total ${label}`;

  // @ts-expect-error TODO: dateRange format is not correct
  const enabled = !!reportingGroup && !!dateRange?.from && !!dateRange?.to;

  const onError = () =>
    toast.push({
      intent: "error",
      description:
        "There was an error fetching your facility emissions over time",
    });

  const barChartRequest = {
    page: 1,
    pageSize: NODE_API_MAX_PAGE_SIZE,
    sortBy: "year_month",
    sortDirection: "desc",
    filters: {
      measurement_type: {
        $in: ["mass_ch4", "mass_co2", "mass_co2eq", "mass_n2o"],
      },
      reporting_group: reportingGroup,
      year_month: {
        // @ts-expect-error TODO: date format is not correct
        $in: getIntervalInMonthsFormatted(hydrateDateRange(dateRange)),
      },
      isAlreadyFormatted: true,
    },
    groupBy: ["year_month"],
  };

  const barChartQuery = useQuery({
    queryKey: ["calculatorResults", barChartRequest],
    queryFn: () => CalculatorResultsDomain.getList(barChartRequest as any),
    select: ({ data }) => data,
    enabled,
    onError,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const metricTileRequest = {
    page: 1,
    pageSize: 1,
    filters: {
      reporting_group: reportingGroup,
      year_month: {
        // @ts-expect-error TODO: date format is not correct
        $in: getIntervalInMonthsFormatted(hydrateDateRange(dateRange)),
      },
      isAlreadyFormatted: true,
    },
  };

  const metricTileQuery = useQuery({
    queryKey: ["calculatorResults", metricTileRequest],
    queryFn: () => CalculatorResultsDomain.getList(metricTileRequest as any),
    select: ({ data: [{ measurement }] }) => measurement,
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const facilityEmissions = useMemo(() => {
    if (!barChartQuery.data) {
      return [];
    }

    try {
      // patches any missing pollutants with 0 instead of undefined so that the chart doesn't break
      const data = barChartQuery.data.map((record) => {
        pollutants.forEach(({ mass }) => {
          if (!record.measurement[mass]) {
            record.measurement[mass] = 0;
          }
        });

        return record;
      });

      return data
        .reduce((total: DataSetType[], record): DataSetType[] => {
          const label = record["equipment.type.name"];
          const date = parse(record.year_month ?? "", "yyyyMM", new Date());

          const setIndex = total.findIndex(
            ({ label: currentLabel }) => currentLabel === label
          );

          if (setIndex >= 0) {
            // @ts-expect-error TODO: data format is incorrect
            total[setIndex].data.push({
              ...record,
              date,
            });

            return total;
          }

          return [
            // @ts-expect-error TODO: data format is incorrect
            ...total,
            {
              // @ts-expect-error TODO: data format is incorrect
              label,
              variant: "bar",
              axis: "left",
              data: [
                // @ts-expect-error TODO: data format is incorrect
                {
                  ...record,
                  date,
                },
              ],
            },
          ];
        }, [] as DataSetType[])
        .map(({ label, data, ...restSet }) => ({
          ...restSet,
          label,
          data: data.sort(sortFunction),
        }));
    } catch (error) {
      onError();
      return [];
    }
  }, [barChartQuery.data]);
  return (
    <>
      <div
        style={{
          display: "flex",
          width: "100%",
          margin: "16px 0px",
        }}
      >
        <MetricTile
          style={{ flexGrow: 1 }}
          isLoading={metricTileQuery.isLoading}
          title={title}
          value={(props) => (
            <NumberDataDisplay
              {...props}
              value={metricTileQuery.data?.[mass]}
              formattingOpts={precision ? { fractionDigits: precision } : {}}
            />
          )}
          unit={unit}
        />
      </div>
      <XYChart
        // @ts-expect-error, the prop does exist but it's getting lost in the sauce due to the nesting
        height={BAR_CHART_HEIGHT}
        sets={facilityEmissions}
        isLegendVisible={false}
        isBrushVisible={false}
        leftAxis={{
          label: `${label} (${unit})`,
          valueAccessor: (data: DataPoint) => data.measurement[mass],
          valueRenderer: (data: DataPoint) =>
            formatNumberValueAndUnit(
              { value: data.measurement[mass], unit },
              { overrideOpts: { minimumFractionDigits: precision ?? 0 } }
            ) ?? "-",
        }}
        bottomAxis={{
          compare: sortFunction,
          tickRenderer: (date: string) => format(new Date(date), "MMM yyyy"),
          valueAccessor: ({ date }: DataPoint) => date,
        }}
      />
    </>
  );
};
